import React, { useEffect, useState, useMemo } from "react";
import DashboardLayout from "../DashboardLayout/DasboardLayout";
import { API_URL } from "../config";
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  Activity,
  Download,
  RefreshCw,
  Calendar,
  Filter,
  BarChart3,
  PieChart,
  Users,
  Car,
  AlertCircle,
  CreditCard,
  Receipt,
  Target,
  Zap,
  Award,
  ChevronUp,
  ChevronDown
} from "lucide-react";

// Collapsible Tooltip Component
const ReportTooltip = ({ title, children }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left"
      >
        <div className="flex items-center space-x-2">
          <AlertCircle className="text-blue-500" size={20} />
          <h3 className="text-lg font-semibold text-blue-900">{title}</h3>
        </div>
        {isExpanded ? <ChevronUp className="text-blue-500" size={20} /> : <ChevronDown className="text-blue-500" size={20} />}
      </button>
      {isExpanded && (
        <div className="mt-4 text-sm text-blue-800 space-y-2">
          {children}
        </div>
      )}
    </div>
  );
};

const RevenueBreakdownReport = () => {
  const [trips, setTrips] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState("all");
  const [expandedSections, setExpandedSections] = useState({});

  // Get date range based on filter
  const getDateRange = (filter) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (filter) {
      case "today":
        return {
          start: today,
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case "7days":
        return {
          start: new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000),
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case "14days":
        return {
          start: new Date(today.getTime() - 13 * 24 * 60 * 60 * 1000),
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case "30days":
        return {
          start: new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000),
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      default:
        return null;
    }
  };

  // Filter trips based on date range
  const filterTripsByDate = (trips, dateRange) => {
    if (!dateRange) return trips;
    
    return trips.filter(trip => {
      if (!trip.order_start_datetime) return false;
      const tripDate = new Date(trip.order_start_datetime);
      return tripDate >= dateRange.start && tripDate <= dateRange.end;
    });
  };

  // Fetch all data
  const fetchData = async () => {
    setLoading(true);
    try {
      const [tripsRes, driversRes, customersRes] = await Promise.all([
        fetch(`${API_URL}/trip/`),
        fetch(`${API_URL}/driver/`),
        fetch(`${API_URL}/customerdetails/`)
      ]);

      const [tripsData, driversData, customersData] = await Promise.all([
        tripsRes.json(),
        driversRes.json(),
        customersRes.json()
      ]);

      console.log("Revenue breakdown report - Trips data:", tripsData);

      setTrips(tripsData || []);
      setDrivers(driversData || []);
      setCustomers(customersData || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Process revenue data
  const revenueAnalysis = useMemo(() => {
    const dateRange = getDateRange(timeFilter);
    const filteredTrips = filterTripsByDate(trips, dateRange);
    
    console.log(`Revenue breakdown filter: ${timeFilter}, Total trips: ${trips.length}, Filtered trips: ${filteredTrips.length}`);

    // Separate trips by type
    const privateTrips = filteredTrips.filter(trip => 
      trip.trip_type === 'Private' || trip.service_type === 'Private'
    );
    const rideshareTrips = filteredTrips.filter(trip => 
      trip.trip_type === 'Rideshare' || trip.service_type === 'Rideshare' || trip.rideshare_id
    );

    // Initialize revenue data structures
    const revenueByServiceType = {
      private: { count: 0, revenue: 0, avgRevenue: 0 },
      rideshare: { count: 0, revenue: 0, avgRevenue: 0 },
      unknown: { count: 0, revenue: 0, avgRevenue: 0 }
    };

    const revenueByPriceRange = {
      '0-10': { count: 0, revenue: 0, avgRevenue: 0 },
      '10-25': { count: 0, revenue: 0, avgRevenue: 0 },
      '25-50': { count: 0, revenue: 0, avgRevenue: 0 },
      '50-100': { count: 0, revenue: 0, avgRevenue: 0 },
      '100+': { count: 0, revenue: 0, avgRevenue: 0 }
    };

    const revenueByHour = {};
    const revenueByDay = {};
    const revenueByDriver = {};
    const revenueByCustomer = {};

    filteredTrips.forEach(trip => {
      const revenue = parseFloat(trip.delivery_cost_proposed) || 0;
      const tripDate = new Date(trip.order_start_datetime);
      const hour = tripDate.getHours();
      const dayOfWeek = tripDate.getDay();
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayName = dayNames[dayOfWeek];

      // Service type revenue
      let serviceType = 'unknown';
      if (trip.trip_type === 'Private' || trip.service_type === 'Private') {
        serviceType = 'private';
      } else if (trip.trip_type === 'Rideshare' || trip.service_type === 'Rideshare' || trip.rideshare_id) {
        serviceType = 'rideshare';
      }
      
      revenueByServiceType[serviceType].count++;
      revenueByServiceType[serviceType].revenue += revenue;

      // Price range revenue
      let priceRange;
      if (revenue < 10) priceRange = '0-10';
      else if (revenue < 25) priceRange = '10-25';
      else if (revenue < 50) priceRange = '25-50';
      else if (revenue < 100) priceRange = '50-100';
      else priceRange = '100+';
      
      revenueByPriceRange[priceRange].count++;
      revenueByPriceRange[priceRange].revenue += revenue;

      // Hourly revenue
      if (!revenueByHour[hour]) {
        revenueByHour[hour] = { count: 0, revenue: 0, avgRevenue: 0 };
      }
      revenueByHour[hour].count++;
      revenueByHour[hour].revenue += revenue;

      // Daily revenue
      if (!revenueByDay[dayName]) {
        revenueByDay[dayName] = { count: 0, revenue: 0, avgRevenue: 0 };
      }
      revenueByDay[dayName].count++;
      revenueByDay[dayName].revenue += revenue;

      // Driver revenue
      if (trip.driver_id) {
        if (!revenueByDriver[trip.driver_id]) {
          revenueByDriver[trip.driver_id] = { 
            count: 0, 
            revenue: 0, 
            avgRevenue: 0,
            driverInfo: null
          };
        }
        revenueByDriver[trip.driver_id].count++;
        revenueByDriver[trip.driver_id].revenue += revenue;
      }

      // Customer revenue
      if (trip.cust_id) {
        if (!revenueByCustomer[trip.cust_id]) {
          revenueByCustomer[trip.cust_id] = { 
            count: 0, 
            revenue: 0, 
            avgRevenue: 0,
            customerInfo: null
          };
        }
        revenueByCustomer[trip.cust_id].count++;
        revenueByCustomer[trip.cust_id].revenue += revenue;
      }
    });

    // Add driver and customer info with better matching
    console.log("Drivers data:", drivers);
    console.log("Revenue by driver keys:", Object.keys(revenueByDriver));
    
    drivers.forEach(driver => {
      // Try multiple ID fields for matching
      const driverIds = [
        driver.driver_id,
        driver.id,
        driver._id,
        driver.driverid,
        driver.driverId
      ].filter(id => id); // Remove null/undefined
      
      driverIds.forEach(driverId => {
        if (revenueByDriver[driverId]) {
          revenueByDriver[driverId].driverInfo = driver;
          console.log(`Matched driver ${driverId}:`, driver);
        }
      });
    });

    customers.forEach(customer => {
      // Try multiple ID fields for matching - prioritize customerid
      const customerIds = [
        customer.customerid, // FIXED: Prioritize customerid as it's the correct field
        customer.cust_id,
        customer.id,
        customer._id,
        customer.customerId,
        customer.user_id,
        customer.userId
      ].filter(id => id); // Remove null/undefined
      
      customerIds.forEach(customerId => {
        if (revenueByCustomer[customerId]) {
          revenueByCustomer[customerId].customerInfo = customer;
          console.log(`Matched customer ${customerId}:`, customer);
        }
      });
    });

    // Calculate averages
    Object.keys(revenueByServiceType).forEach(type => {
      const data = revenueByServiceType[type];
      data.avgRevenue = data.count > 0 ? data.revenue / data.count : 0;
    });

    Object.keys(revenueByPriceRange).forEach(range => {
      const data = revenueByPriceRange[range];
      data.avgRevenue = data.count > 0 ? data.revenue / data.count : 0;
    });

    Object.keys(revenueByHour).forEach(hour => {
      const data = revenueByHour[hour];
      data.avgRevenue = data.count > 0 ? data.revenue / data.count : 0;
    });

    Object.keys(revenueByDay).forEach(day => {
      const data = revenueByDay[day];
      data.avgRevenue = data.count > 0 ? data.revenue / data.count : 0;
    });

    Object.keys(revenueByDriver).forEach(driverId => {
      const data = revenueByDriver[driverId];
      data.avgRevenue = data.count > 0 ? data.revenue / data.count : 0;
    });

    Object.keys(revenueByCustomer).forEach(customerId => {
      const data = revenueByCustomer[customerId];
      data.avgRevenue = data.count > 0 ? data.revenue / data.count : 0;
    });

    return {
      filteredTrips,
      privateTrips,
      rideshareTrips,
      revenueByServiceType,
      revenueByPriceRange,
      revenueByHour,
      revenueByDay,
      revenueByDriver,
      revenueByCustomer,
      totalRevenue: filteredTrips.reduce((sum, trip) => sum + (parseFloat(trip.delivery_cost_proposed) || 0), 0)
    };
  }, [trips, drivers, customers, timeFilter]);

  // Get top performers
  const getTopPerformers = (data, metric, limit = 20) => {
    return Object.entries(data)
      .map(([id, stats]) => ({ id, ...stats }))
      .sort((a, b) => b[metric] - a[metric])
      .slice(0, limit);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getFilterLabel = (filter) => {
    switch(filter) {
      case "today": return "Today";
      case "7days": return "Past 7 Days";
      case "14days": return "Past 14 Days";
      case "30days": return "Past 30 Days";
      default: return "All Time";
    }
  };

  const exportData = (data, filename, includeId = false) => {
    const headers = includeId 
      ? ['ID', 'Name', 'Email', 'Phone', 'Trips', 'Revenue', 'Average Revenue']
      : ['Category', 'Trips', 'Revenue', 'Average Revenue'];
    
    const csv = [
      headers,
      ...data.map(item => {
        if (includeId) {
          const info = item.driverInfo || item.customerInfo;
          return [
            item.id,
            info?.name ? `${info.name} ${info.surname || ''}`.trim() : `${info?.first_name || ''} ${info?.last_name || ''}`.trim() || 'Unknown',
            info?.email || 'N/A',
            info?.phone || 'N/A',
            item.count,
            item.revenue?.toFixed(2) || 0,
            item.avgRevenue?.toFixed(2) || 0
          ];
        } else {
          return [
            item.name || item.id || 'Unknown',
            item.count || 0,
            item.revenue?.toFixed(2) || 0,
            item.avgRevenue?.toFixed(2) || 0
          ];
        }
      })
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <RefreshCw className="animate-spin text-blue-500" size={40} />
        </div>
      </DashboardLayout>
    );
  }

  const { 
    filteredTrips, 
    privateTrips, 
    rideshareTrips, 
    revenueByServiceType, 
    revenueByPriceRange, 
    revenueByHour, 
    revenueByDay, 
    revenueByDriver, 
    revenueByCustomer, 
    totalRevenue 
  } = revenueAnalysis;

  const topDriversByRevenue = getTopPerformers(revenueByDriver, 'revenue');
  const topCustomersByRevenue = getTopPerformers(revenueByCustomer, 'revenue');

  return (
    <DashboardLayout>
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Revenue Breakdown Report
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Comprehensive revenue analysis by service type, price range, time, and customer segments
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <DollarSign className="text-gray-500" size={20} />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {getFilterLabel(timeFilter)}
              </span>
            </div>
          </div>

          {/* Time Filter Buttons */}
          <div className="flex flex-wrap gap-2 mb-6">
            {[
              { value: "all", label: "All Time" },
              { value: "today", label: "Today" },
              { value: "7days", label: "Past 7 Days" },
              { value: "14days", label: "Past 14 Days" },
              { value: "30days", label: "Past 30 Days" }
            ].map(filter => (
              <button
                key={filter.value}
                onClick={() => setTimeFilter(filter.value)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  timeFilter === filter.value
                    ? "bg-blue-600 text-white shadow-lg transform scale-105"
                    : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {/* Report Tooltip */}
        <ReportTooltip title="Revenue Breakdown Report - Business Value & Features">
          <div className="space-y-3">
            <div>
              <strong className="text-blue-900">💰 Report Overview:</strong>
              <p>Comprehensive revenue analysis by service type, price range, time patterns, and customer segments with detailed financial insights.</p>
            </div>
            
            <div>
              <strong className="text-blue-900">🎯 Key Business Questions Answered:</strong>
              <ul className="list-disc ml-4 space-y-1">
                <li><strong>Revenue Optimization:</strong> Which service generates more revenue? What price ranges drive most income? When do we make most money?</li>
                <li><strong>Customer Value:</strong> Who are my most valuable customers? Which customers should I retain? Customer lifetime value analysis?</li>
                <li><strong>Driver Performance:</strong> Which drivers generate most revenue? Who are my top revenue drivers? Driver financial performance?</li>
                <li><strong>Strategic Planning:</strong> How is revenue distributed? What are the revenue patterns? Where should I focus resources?</li>
              </ul>
            </div>
            
            <div>
              <strong className="text-blue-900">✅ Key Features:</strong>
              <ul className="list-disc ml-4 space-y-1">
                <li><strong>Service Type Analysis:</strong> Private vs Rideshare revenue comparison and breakdown</li>
                <li><strong>Price Range Segments:</strong> Revenue distribution by trip value ranges ($0-10, $10-25, $25-50, $50-100, $100+)</li>
                <li><strong>Time-Based Revenue:</strong> Hourly and daily revenue patterns with visual heatmaps</li>
                <li><strong>Top Performers:</strong> Highest revenue drivers and customers with detailed profiles</li>
                <li><strong>Export Functionality:</strong> CSV download for all revenue data and analysis</li>
                <li><strong>Real-Time Data:</strong> Live API calls with 30-second refresh intervals</li>
              </ul>
            </div>
            
            <div>
              <strong className="text-blue-900">💼 Business Impact:</strong>
              <p>This report provides immediate financial value by helping you optimize pricing strategies, identify high-value customers, reward top-performing drivers, maximize revenue during peak times, and make data-driven financial decisions.</p>
            </div>
            
            <div>
              <strong className="text-blue-900">🔍 Data Source:</strong>
              <p>Uses REAL data from live API calls to trips, drivers, and customers endpoints. All revenue calculations are based on actual delivery_cost_proposed values from completed trips.</p>
            </div>
          </div>
        </ReportTooltip>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Total Revenue {timeFilter !== "all" ? `(${getFilterLabel(timeFilter)})` : "(All Time)"}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  ${totalRevenue.toFixed(0)}
                </p>
              </div>
              <DollarSign className="text-green-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Private Revenue</p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  ${revenueByServiceType.private.revenue.toFixed(0)}
                </p>
              </div>
              <Car className="text-blue-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Rideshare Revenue</p>
                <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                  ${revenueByServiceType.rideshare.revenue.toFixed(0)}
                </p>
              </div>
              <Users className="text-orange-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Avg Trip Value</p>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  ${(totalRevenue / filteredTrips.length).toFixed(2)}
                </p>
              </div>
              <Receipt className="text-purple-500" size={24} />
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Trips</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {filteredTrips.length}
                </p>
              </div>
              <Activity className="text-gray-500" size={24} />
            </div>
          </div>
        </div>

        {/* Debug Info */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-semibold text-yellow-800 mb-2">Debug Info:</h3>
          <p className="text-xs text-yellow-700">Filter: {getFilterLabel(timeFilter)}</p>
          <p className="text-xs text-yellow-700">Total Trips: {trips.length} | Filtered: {filteredTrips.length}</p>
          <p className="text-xs text-yellow-700">Private Trips: {privateTrips.length} | Rideshare Trips: {rideshareTrips.length}</p>
          <p className="text-xs text-yellow-700">Total Revenue: ${totalRevenue.toFixed(2)} | Avg Trip: ${(totalRevenue / filteredTrips.length).toFixed(2)}</p>
        </div>

        {/* Report Sections */}
        <div className="space-y-6">
          {/* Revenue by Service Type */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <PieChart className="mr-2 text-green-500" />
              Revenue by Service Type
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Object.entries(revenueByServiceType).map(([type, data]) => {
                    const percentage = totalRevenue > 0 ? (data.revenue / totalRevenue * 100) : 0;
                    const colors = {
                      private: 'blue',
                      rideshare: 'orange',
                      unknown: 'gray'
                    };
                    const color = colors[type];
                    
                    return (
                      <div key={type} className="border rounded-lg p-4 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            {type === 'private' ? <Car className={`text-${color}-500`} size={20} /> :
                             type === 'rideshare' ? <Users className={`text-${color}-500`} size={20} /> :
                             <AlertCircle className={`text-${color}-500`} size={20} />}
                            <span className="font-semibold text-gray-900 dark:text-white capitalize">
                              {type} Rides
                            </span>
                          </div>
                          <span className={`bg-${color}-100 text-${color}-800 px-2 py-1 rounded text-sm font-medium`}>
                            {percentage.toFixed(1)}%
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          <p>Revenue: ${data.revenue.toFixed(2)}</p>
                          <p>Trips: {data.count}</p>
                          <p>Avg: ${data.avgRevenue.toFixed(2)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Revenue by Price Range */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <Target className="mr-2 text-purple-500" />
                Revenue by Price Range
              </div>
              <button
                onClick={() => exportData(Object.entries(revenueByPriceRange).map(([name, data]) => ({ name, ...data })), 'revenue-by-price-range.csv')}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <Download size={16} />
                <span className="text-sm">Export</span>
              </button>
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(revenueByPriceRange).map(([range, data]) => {
                    const percentage = totalRevenue > 0 ? (data.revenue / totalRevenue * 100) : 0;
                    const colors = {
                      '0-10': 'green',
                      '10-25': 'blue',
                      '25-50': 'purple',
                      '50-100': 'orange',
                      '100+': 'red'
                    };
                    const color = colors[range];
                    
                    return (
                      <div key={range} className="border rounded-lg p-4 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <DollarSign className={`text-${color}-500`} size={20} />
                            <span className="font-semibold text-gray-900 dark:text-white">
                              ${range}
                            </span>
                          </div>
                          <span className={`bg-${color}-100 text-${color}-800 px-2 py-1 rounded text-sm font-medium`}>
                            {percentage.toFixed(1)}%
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          <p>Revenue: ${data.revenue.toFixed(2)}</p>
                          <p>Trips: {data.count}</p>
                          <p>Avg: ${data.avgRevenue.toFixed(2)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Top Revenue Drivers */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <Award className="mr-2 text-yellow-500" />
                Top Revenue Drivers
              </div>
              <button
                onClick={() => exportData(topDriversByRevenue, 'top-revenue-drivers.csv', true)}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <Download size={16} />
                <span className="text-sm">Export</span>
              </button>
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Rank</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Driver</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Trips</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Revenue</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Avg Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {topDriversByRevenue.map((driver, index) => (
                      <tr key={driver.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                          <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-medium">
                            #{index + 1}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          {driver.driverInfo?.name || `${driver.driverInfo?.first_name || ''} ${driver.driverInfo?.last_name || ''}`.trim() || 
                           `Driver #${driver.id}` || 'Unknown'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          {driver.driverInfo?.email || driver.driverInfo?.phone || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{driver.count}</td>
                        <td className="px-4 py-3 text-sm font-medium text-green-600">${driver.revenue.toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">${driver.avgRevenue.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Top Revenue Customers */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <Award className="mr-2 text-blue-500" />
                Top Revenue Customers
              </div>
              <button
                onClick={() => exportData(topCustomersByRevenue, 'top-revenue-customers.csv', true)}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <Download size={16} />
                <span className="text-sm">Export</span>
              </button>
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Rank</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Trips</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Revenue</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Avg Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {topCustomersByRevenue.map((customer, index) => (
                      <tr key={customer.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                            #{index + 1}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          {customer.customerInfo?.name ? `${customer.customerInfo.name} ${customer.customerInfo.surname || ''}`.trim() : 
                           `${customer.customerInfo?.first_name || ''} ${customer.customerInfo?.last_name || ''}`.trim() || 
                           `Customer #${customer.id}` || 'Unknown'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                          {customer.customerInfo?.email || customer.customerInfo?.phone || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{customer.count}</td>
                        <td className="px-4 py-3 text-sm font-medium text-green-600">${customer.revenue.toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">${customer.avgRevenue.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Revenue by Hour */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Zap className="mr-2 text-yellow-500" />
              Revenue by Hour
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {Array.from({ length: 24 }, (_, hour) => {
                    const hourData = revenueByHour[hour];
                    const revenue = hourData ? hourData.revenue : 0;
                    const maxRevenue = Math.max(...Object.values(revenueByHour).map(h => h.revenue));
                    const intensity = maxRevenue > 0 ? (revenue / maxRevenue) : 0;
                    
                    let bgColor = 'bg-gray-100';
                    if (intensity > 0.8) bgColor = 'bg-green-500';
                    else if (intensity > 0.6) bgColor = 'bg-green-400';
                    else if (intensity > 0.4) bgColor = 'bg-yellow-400';
                    else if (intensity > 0.2) bgColor = 'bg-orange-400';
                    else if (intensity > 0) bgColor = 'bg-red-400';
                    
                    return (
                      <div
                        key={hour}
                        className={`${bgColor} text-white rounded p-2 text-center`}
                        title={`${hour}:00 - $${revenue.toFixed(2)}`}
                      >
                        <div className="text-xs font-medium">{hour}:00</div>
                        <div className="text-xs">${revenue.toFixed(0)}</div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center justify-center space-x-4 mt-4 text-xs text-gray-600 dark:text-gray-400">
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-gray-100 rounded"></div>
                    <span>No Revenue</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-red-400 rounded"></div>
                    <span>Low</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-orange-400 rounded"></div>
                    <span>Medium</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-yellow-400 rounded"></div>
                    <span>High</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-green-400 rounded"></div>
                    <span>Very High</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-green-500 rounded"></div>
                    <span>Peak</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Revenue by Day */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Calendar className="mr-2 text-indigo-500" />
              Revenue by Day
            </h2>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(revenueByDay).map(([day, data]) => {
                    const percentage = totalRevenue > 0 ? (data.revenue / totalRevenue * 100) : 0;
                    
                    return (
                      <div key={day} className="border rounded-lg p-4 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {day}
                          </span>
                          <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded text-sm font-medium">
                            {percentage.toFixed(1)}%
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          <p>Revenue: ${data.revenue.toFixed(2)}</p>
                          <p>Trips: {data.count}</p>
                          <p>Avg: ${data.avgRevenue.toFixed(2)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default RevenueBreakdownReport;
